import React from 'react';
import {
    Users,
    UserPlus,
    Zap,
    Target,
    AlertTriangle,
    Sparkles,
    LayoutGrid,
    Search as SearchIcon
} from 'lucide-react';
import './Team.css';

const Team = () => {
    // Hardcoded high-fidelity data as requested
    const teamStats = [
        { label: 'Total Members', value: '8', icon: <Users size={18} />, color: '#FF5526', bg: '#FFF2ED' },
        { label: 'Active on RFPs', value: '8', icon: <LayoutGrid size={18} />, color: '#FF5526', bg: '#FFF2ED' },
        { label: 'Avg Workload', value: '64%', icon: <Zap size={18} />, color: '#10B981', bg: '#ECFDF5' },
        { label: 'Top Win Rate', value: '82%', icon: <Target size={18} />, color: '#FF5526', bg: '#F5F3FF' },
        { label: 'Overloaded', value: '1', icon: <AlertTriangle size={18} />, color: '#EF4444', bg: '#FEF2F2' },
    ];

    const aiInsights = [
        { 
            priority: 'High', 
            text: 'Lisa Park is overloaded — reassign 2 requirements to Alex Kim for balance',
            type: 'priority-high' 
        },
        { 
            priority: 'Medium', 
            text: 'Sarah Chen has the highest win rate at 82% — consider for strategic proposals',
            type: 'priority-medium' 
        },
        { 
            priority: 'Low', 
            text: 'Tom Wilson has low utilization at 40% — assign to Metro Transit intake',
            type: 'priority-low' 
        }
    ];

    const teamMembers = [
        {
            id: 1,
            name: 'James Donovan',
            role: 'Bid Manager',
            status: 'Busy',
            active: 4,
            winRate: '70%',
            avgReview: '1.2d',
            workload: 75,
            skills: ['Strategy', 'Pricing', 'Leadership'],
            color: '#FF5526'
        },
        {
            id: 2,
            name: 'Sarah Chen',
            role: 'Technical Lead',
            status: 'Available',
            active: 3,
            winRate: '82%',
            avgReview: '0.8d',
            workload: 60,
            skills: ['Cloud', 'Architecture', 'APIs'],
            color: '#10B981'
        },
        {
            id: 3,
            name: 'Lisa Park',
            role: 'Compliance Specialist',
            status: 'Overloaded',
            active: 5,
            winRate: '75%',
            avgReview: '1.5d',
            workload: 90,
            skills: ['GDPR', 'SOC 2', 'ISO 27001'],
            color: '#EF4444'
        },
        {
            id: 4,
            name: 'Alex Kim',
            role: 'Solutions Architect',
            status: 'Available',
            active: 3,
            winRate: '75%',
            avgReview: '1.0d',
            workload: 55,
            skills: ['System Design', 'Integration', 'Security'],
            color: '#FF5526'
        },
        {
            id: 5,
            name: 'Tom Wilson',
            role: 'Senior Writer',
            status: 'Available',
            active: 2,
            winRate: '80%',
            avgReview: '0.9d',
            workload: 40,
            skills: ['Technical Writing', 'Editing', 'Content'],
            color: '#F59E0B'
        },
        {
            id: 6,
            name: 'Priya Sharma',
            role: 'Pricing Analyst',
            status: 'Busy',
            active: 4,
            winRate: '71%',
            avgReview: '1.3d',
            workload: 80,
            skills: ['Financial Modeling', 'BOQ', 'Analytics'],
            color: '#FF5526'
        },
        {
            id: 7,
            name: 'Riya Patel',
            role: 'Project Manager',
            status: 'Available',
            active: 3,
            winRate: '71%',
            avgReview: '1.1d',
            workload: 65,
            skills: ['Planning', 'Coordination', 'Risk Mgmt'],
            color: '#EC4899'
        },
        {
            id: 8,
            name: 'Mike Ross',
            role: 'Technical Writer',
            status: 'Available',
            active: 2,
            winRate: '65%',
            avgReview: '1.4d',
            workload: 45,
            skills: ['Documentation', 'Templates', 'UX Writing'],
            color: '#0EA5E9'
        }
    ];

    const getInitials = (name) => {
        return name.split(' ').map(n => n[0]).join('').toUpperCase();
    };

    const getWorkloadColor = (pct) => {
        if (pct >= 85) return '#EF4444';
        if (pct >= 70) return '#F59E0B';
        return '#10B981';
    };

    return (
        <div className="team-page">
            <header className="page-header">
                <div>
                    <h1>Team & Workload</h1>
                    <p className="subtitle">Manage team members and balance workload across proposals</p>
                </div>
                <div className="header-actions">
                    <button className="secondary-btn">Auto-Balance</button>
                    <button className="primary-btn"><UserPlus size={18} /> Add Member</button>
                </div>
            </header>

            <section className="stats-grid">
                {teamStats.map((stat, index) => (
                    <div key={index} className="stat-card">
                        <div className="stat-icon" style={{ backgroundColor: stat.bg, color: stat.color }}>
                            {stat.icon}
                        </div>
                        <div className="stat-info">
                            <span className="stat-label">{stat.label}</span>
                            <span className="stat-value">{stat.value}</span>
                        </div>
                    </div>
                ))}
            </section>

            <section className="ai-insights-section">
                <div className="ai-header">
                    <Sparkles size={18} className="text-indigo-500" />
                    <h2>AI Workload Insights</h2>
                </div>
                <div className="insights-row">
                    {aiInsights.map((insight, index) => (
                        <div key={index} className="insight-card">
                            <span className={`insight-priority ${insight.type}`}>
                                {insight.priority}
                            </span>
                            <p className="insight-text">{insight.text}</p>
                        </div>
                    ))}
                </div>
            </section>

            <div className="team-container">
                <div className="team-filter-bar">
                    <h2>Team Members ({teamMembers.length})</h2>
                    <div className="search-input-wrapper">
                        <SearchIcon size={18} />
                        <input type="text" placeholder="Search proposals, requirements, content..." />
                    </div>
                </div>

                <div className="team-grid">
                    {teamMembers.map((member) => (
                        <div key={member.id} className="member-card">
                            <div className="member-header">
                                <div className="member-profile">
                                    <div className="member-avatar" style={{ backgroundColor: member.color }}>
                                        {getInitials(member.name)}
                                    </div>
                                    <div className="member-main-info">
                                        <h3>{member.name}</h3>
                                        <p className="member-role">{member.role}</p>
                                    </div>
                                </div>
                                <div className={`status-indicator status-${member.status.toLowerCase()}`}>
                                    <div className="dot" />
                                    {member.status}
                                </div>
                            </div>

                            <div className="member-metrics">
                                <div className="metric-item">
                                    <span className="metric-val">{member.active}</span>
                                    <span className="metric-label">Active</span>
                                </div>
                                <div className="metric-item">
                                    <span className="metric-val">{member.winRate}</span>
                                    <span className="metric-label">Win Rate</span>
                                </div>
                                <div className="metric-item">
                                    <span className="metric-val">{member.avgReview}</span>
                                    <span className="metric-label">Avg Review</span>
                                </div>
                            </div>

                            <div className="workload-section">
                                <div className="workload-header">
                                    <span className="workload-label">Workload</span>
                                    <span className="workload-pct" style={{ color: getWorkloadColor(member.workload) }}>
                                        {member.workload}%
                                    </span>
                                </div>
                                <div className="progress-bar-container">
                                    <div 
                                        className="progress-fill" 
                                        style={{ 
                                            width: `${member.workload}%`, 
                                            backgroundColor: getWorkloadColor(member.workload) 
                                        }} 
                                    />
                                </div>
                            </div>

                            <div className="skill-tags">
                                {member.skills.map((skill, i) => (
                                    <span key={i} className="skill-tag">{skill}</span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Team;

